import { DeleteIcon, DownloadIcon, EyeIcon } from '@/components';
import { ClipboardList, Clock, Copy, MapPin } from 'lucide-react';
import { DRIVER, LOCATION, ORDER_ACTIONS, ORDER_STATUS, PICKUP, ROUTE } from '@/config';
import { ColumnsType } from 'antd/es/table';
import { Dropdown, message, notification } from 'antd';
import { ITable } from '@/components/TableAction/TableAction';
import { PermissionName, PickupItem } from '@/types';
import { useCancelPickupById, useFetchPickupLabel, useGeneratePickupLabel } from '@/hooksApi';
import { QueryClient } from '@tanstack/react-query';
import { triggerDownload, formatPhoneDisplay} from '@/utils';
import { useState } from 'react';
import { useNavigateWithPrevPageState } from '@/hooks/navigateWithPrevPageState';
import { useTranslation } from 'react-i18next';
import { usePollingMechanismStore } from '@/stores';
import { ProcessNameEnum } from '@/stores/pollingMechanismStore';
import PickupSlotCell from './PickupSlotCell';
import useChangeSlotStore from '@/stores/useChangeSlotStore';
import { isSlotEditable } from './slotEditability';

export interface OrderData extends ITable {
  pickupTime: string;
  pickupLocation: string;
  location: string;
  platform_code: string;
  driver: {
    phone: string;
    name: string;
    assign: boolean;
  };
  status: string;
  driver_tasks_Icon: string;
  missed_orders_count: number;
  orders_count: number;
  slot?: PickupItem['slot'];
  slot_state?: string | null;
  is_next_day?: boolean;
  slot_response_open?: boolean;
}

// Exact rgba status colors for pickup statuses — sourced from getComputedStyle
const PICKUP_STATUS_HSL: Record<string, { bg: string; text: string }> = {
  new: { bg: 'rgba(168,85,247,0.12)', text: 'rgb(147,51,234)' },
  scheduled: { bg: 'rgba(66,140,215,0.12)', text: 'rgb(43,107,171)' },
  collecting: { bg: 'rgba(226,140,54,0.12)', text: 'rgb(182,107,32)' },
  'out-for-collecting': { bg: 'rgba(226,140,54,0.12)', text: 'rgb(182,107,32)' },
  'out-for-collection': { bg: 'rgba(226,140,54,0.12)', text: 'rgb(182,107,32)' },
  collected: { bg: 'rgba(49,196,127,0.12)', text: 'rgb(39,155,101)' },
  'bulk-collected': { bg: 'rgba(49,196,127,0.12)', text: 'rgb(39,155,101)' },
  cancelled: { bg: 'rgba(239,68,68,0.12)', text: 'rgb(220,38,38)' },
};

const getInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const nameToColor = (name?: string): string => {
  if (!name) return '#9CA3AF';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 45%)`;
};

export const getColumns = (
  t: (key: string) => string,
  queryClient: QueryClient,
  openEntityTicketModal: (platformCode?: string) => void,
  hasPermission: (...permissions: string[]) => boolean,
) => {
  const columns: ColumnsType<OrderData> = [
    {
      title: String(t('platform_code')),
      dataIndex: PICKUP,
      key: PICKUP,
      sorter: true,
      render: (_, record) => (
        <div className='flex flex-col gap-0.5'>
          <div className='flex items-center gap-1.5'>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'rgb(57,57,60)',
              }}
            >
              {record.platform_code ?? '---'}
            </span>
            {record.platform_code && (
              <Copy
                size={12}
                className='cursor-pointer text-gray-400 hover:text-gray-600 shrink-0'
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(record.platform_code ?? '');
                  message.success(String(t('copied')));
                }}
              />
            )}
          </div>
          <span style={{ fontSize: '11px', color: 'rgb(115,123,140)' }}>{record.pickupTime}</span>
        </div>
      ),
    },
    {
      title: String(t('drop_off_location')),
      dataIndex: LOCATION,
      key: LOCATION,
      render: (text: string) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MapPin size={14} color='rgba(115,123,140,0.6)' />
          <span style={{ fontSize: '12px', fontWeight: 400, color: 'rgb(115,123,140)' }}>
            {text ?? '---'}
          </span>
        </span>
      ),
    },
    {
      title: String(t('driver')),
      dataIndex: DRIVER,
      key: DRIVER,
      render: (record: { name?: string; phone?: string }, fullRow: OrderData) => {
        const hasDriver = Boolean(record?.name?.trim());
        const rowStatus = (fullRow?.status || '').toLowerCase();
        // Show "assigning driver" animation only for 'new' status pickups
        const showAssigningAnimation = !hasDriver && rowStatus === 'new';

        if (!hasDriver && !showAssigningAnimation) {
          // Non-new unassigned: plain brown circle + blank
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#D2B48C',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '13px', color: 'rgb(115,123,140)' }}>---</span>
            </div>
          );
        }

        if (!hasDriver) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Avatar skeleton with orbital ring — only for 'new' status */}
              <div style={{ position: 'relative', width: 32, height: 32, flexShrink: 0 }}>
                <div
                  style={{
                    position: 'absolute',
                    inset: -3,
                    borderRadius: '50%',
                    border: '2px solid transparent',
                    borderTopColor: '#EBDD4C',
                    borderRightColor: 'rgba(235,221,76,0.25)',
                    animation: 'boxy-orbit 1.4s linear infinite',
                  }}
                />
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #F3F4F6 0%, #E9EAEC 100%)',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%)',
                      animation: 'boxy-shimmer 1.8s ease-in-out infinite',
                    }}
                  />
                </div>
              </div>
              {/* Text skeleton + pulsing dots */}
              <div className='flex flex-col gap-1.5' style={{ minWidth: 0 }}>
                <div
                  style={{
                    height: 10,
                    width: 88,
                    borderRadius: 6,
                    background: 'linear-gradient(90deg, #EFEFEF 0%, #E4E4E4 50%, #EFEFEF 100%)',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%)',
                      animation: 'boxy-shimmer 1.8s ease-in-out 0.15s infinite',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {[0, 0.22, 0.44].map((delay, i) => (
                    <div
                      key={i}
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        background: '#EBDD4C',
                        animation: `boxy-pulse-dot 1.2s ease-in-out ${delay}s infinite`,
                      }}
                    />
                  ))}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      color: '#9CA3AF',
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {String(t('assigning_driver'))}
                  </span>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: nameToColor(record?.name),
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {getInitials(record?.name)}
            </div>
            <div className='flex flex-col gap-0.5'>
              <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(57,57,60)' }}>
                {record.name!.trim()}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 400, color: 'rgb(115,123,140)' }}>
                {formatPhoneDisplay(record?.phone) || '---'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: String(t('status')),
      dataIndex: ORDER_STATUS,
      key: ORDER_STATUS,
      render: (text: string) => {
        if (!text) return <span style={{ color: 'rgb(115,123,140)' }}>---</span>;
        const slug = text.toLowerCase();
        const hsl = PICKUP_STATUS_HSL[slug] ?? {
          bg: 'hsla(0,0%,60%,0.10)',
          text: 'hsl(0,0%,38%)',
        };
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 11,
              fontWeight: 600,
              color: hsl.text,
              backgroundColor: hsl.bg,
              padding: '4px 10px',
              borderRadius: 9999,
              lineHeight: '16.5px',
              whiteSpace: 'nowrap',
            }}
          >
            {String(t(text))}
          </span>
        );
      },
    },
    {
      title: String(t('not_received')),
      dataIndex: 'missed_orders_count',
      key: 'missed_orders_count',
      render: (count: number | null) => (
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: count ? 'rgb(220,40,40)' : 'rgb(115,123,140)',
          }}
        >
          {count ?? '—'}
        </span>
      ),
    },
    {
      title: String(t('pickup_time_slot')),
      key: 'slot',
      render: (_: unknown, record: OrderData) => (
        <PickupSlotCell
          uid={record.key as string}
          slot={record.slot}
          slotState={record.slot_state}
          status={record.status}
          isNextDay={record.is_next_day}
          slotResponseOpen={record.slot_response_open}
        />
      ),
    },
    {
      title: String(t('actions')),
      key: ORDER_ACTIONS,
      render: (_: unknown, record: OrderData) => {
        return (
          <PickupAction
            id={record.key}
            status={record.status}
            slot={record.slot}
            slotState={record.slot_state}
            queryClient={queryClient}
            platformCode={record.platform_code}
            openEntityTicketModal={openEntityTicketModal}
            hasPermission={hasPermission}
          />
        );
      },
      width: '10%',
      fixed: 'right' as 'right' | undefined,
    },
  ];

  return columns;
};

const PickupAction = ({
  id,
  queryClient,
  status,
  slot,
  slotState,
  openEntityTicketModal,
  platformCode,
  hasPermission,
}: {
  id: string | undefined;
  status: string;
  slot?: PickupItem['slot'];
  slotState?: string | null;
  queryClient: QueryClient;
  openEntityTicketModal: (platformCode?: string, uid?: string) => void;
  platformCode: string;
  hasPermission: (...permissions: string[]) => boolean;
}) => {
  const [idSelected, setIdSelected] = useState<string | null>();
  const openChangeSlot = useChangeSlotStore((state) => state.openChangeSlot);

  const { isLoading: isGeneratingPickupLabel, mutateAsync: generatePickupLabel } =
    useGeneratePickupLabel();

  const { mutateAsync: fetchPickupLabel } = useFetchPickupLabel();

  const { mutate: CancelPickupById, isLoading: cancelPickupLoading } = useCancelPickupById();

  const { startPolling } = usePollingMechanismStore();

  const { t } = useTranslation();
  const navigateWithPrevPageState = useNavigateWithPrevPageState();

  const checkRefetchingPickupLabel = async () => {
    try {
      const data = await fetchPickupLabel(id as string);
      if (data.object.status === 'SUCCESS') {
        notification.success({
          message: t('download_ready'),
          description: t('your_file_is_downloading'),
          showProgress: true,
          duration: 5,
        });
        triggerDownload(data.object.file.abs_url, data.object.file.filename);

        setTimeout(() => {
          setIdSelected(null);
        }, 2000);
        return true;
      }
      if (data.object.status === 'FAILED') {
        notification.error({
          message: t('operation_failed_please_try_again'),
          duration: 5,
        });
        setIdSelected(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Download failed', error);
      setIdSelected(null);
      return true;
    }
  };

  const handleGenerateManifest = async () => {
    setIdSelected(id);
    await generatePickupLabel(id as string);
    startPolling(
      ProcessNameEnum.GENERATE_LABEL,
      {
        fileName: 'label',
        toastTextKey: 'preparing_pickup_label',
      },
      checkRefetchingPickupLabel,
    );
  };

  const isCancellable = ['new', 'scheduled', 'out-for-collecting', 'out-for-collection'].includes(
    status || '',
  );

  const canChangeTimeSlot = isSlotEditable(slotState, status);

  const moreMenuItems = [
    {
      key: 'view',
      label: (
        <span className='flex items-center gap-2 text-[13px]'>
          <EyeIcon stroke='#939496' fill='#939496' />
          {String(t('view'))}
        </span>
      ),
      onClick: () => navigateWithPrevPageState(`${ROUTE.PICKUPS_DETAILS}/${id}`),
    },
    ...(canChangeTimeSlot
      ? [
          {
            key: 'change_time_slot',
            label: (
              <span className='flex items-center gap-2 text-[13px]'>
                <Clock size={14} />
                {String(t('change_time_slot'))}
              </span>
            ),
            onClick: () =>
              openChangeSlot({
                uid: id as string,
                currentCode: slot?.code,
                currentPreferredTiming: slot?.preferred_timing,
                mode: 'edit',
              }),
          },
        ]
      : []),
    {
      key: 'cancel',
      label: (
        <span
          className='flex items-center gap-2 text-[13px]'
          style={{ color: isCancellable ? undefined : 'rgb(180,180,180)' }}
        >
          <DeleteIcon />
          {String(t('cancel'))}
        </span>
      ),
      disabled: !isCancellable || status === 'collected',
      onClick: () => {
        CancelPickupById(id as string, {
          onSuccess: () => {
            message.success(t('operation'));
            queryClient.refetchQueries(['pickups']);
          },
        });
      },
    },
  ];

  // v2-task-8b (§2.1/§2.4/§6.7): height + radius now come from Tailwind
  // classes (h-8/rounded-lg, the "compact" 32px tier + radius-sm) instead of
  // ad hoc inline borderRadius/height pixel values — the two properties the
  // design-quality checklist flags as "must be a token, never inline".
  // `opacity` stays inline: it's genuinely dynamic per-instance state
  // (permission/loading), not a structural token.
  // v2-polish (§6 checklist #7): spacing/typography (padding, gap, font-size,
  // font-weight, line-height, layout) now come from Tailwind utilities via
  // `pillClass`; only genuinely-dynamic per-instance colors + opacity stay
  // inline. px-2.5=10px, gap-1.5=6px, text-[11px], leading-none=lineHeight:1 —
  // a 1:1 conversion of the former inline values, zero visual change.
  const pillClass =
    'h-8 rounded-lg inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 text-[11px] font-bold leading-none cursor-pointer';
  const btnStyle: React.CSSProperties = {
    backgroundColor: 'rgb(237,224,74)',
    border: 'none',
    color: 'rgb(57,57,60)',
  };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
      <button
        type='button'
        className={pillClass}
        style={{ ...btnStyle, opacity: hasPermission(PermissionName.CREATE_TICKET) ? 1 : 0.5 }}
        onClick={(e) => {
          e.stopPropagation();
          openEntityTicketModal(platformCode, id);
        }}
        disabled={!hasPermission(PermissionName.CREATE_TICKET)}
      >
        <ClipboardList size={12} />
        {String(t('ticket'))}
      </button>
      <button
        type='button'
        className={pillClass}
        style={{ ...btnStyle, opacity: isGeneratingPickupLabel || idSelected === id ? 0.5 : 1 }}
        onClick={(e) => {
          e.stopPropagation();
          handleGenerateManifest();
        }}
        disabled={isGeneratingPickupLabel || idSelected === id}
      >
        <DownloadIcon width={12} height={12} />
        {String(t('labels', { defaultValue: 'Labels' }))}
      </button>
      <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement='bottomRight'>
        <button
          type='button'
          onClick={(e) => e.stopPropagation()}
          className='w-8 h-8 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
          style={{
            padding: 0,
            border: '1px solid rgb(232,230,227)',
            backgroundColor: '#fff',
            color: 'rgb(115,123,140)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            letterSpacing: '0.05em',
          }}
        >
          ⋯
        </button>
      </Dropdown>
    </div>
  );
};
